/* global google */

import { useMap } from "@vis.gl/react-google-maps";
import { useEffect, useMemo } from "react";

import { MapProjectCoordinates } from "../../../../models/shared/mapProjectCoordinates";
// Deep import: the utils barrel transitively pulls in the axios client, which this hook does not need.
import { toCoordinate } from "../../../../utils/location";
import { MapConstants } from "../constants";

type CoordinateType = { lat: number; lng: number };
type ProjectCoordinateType = CoordinateType & {
  name?: string;
};

interface UseMapReturnData {
  coordinatesList: ProjectCoordinateType[];
  center: CoordinateType;
}

export const useKanaMap = (coordinates: MapProjectCoordinates[], fullWidth: boolean | undefined): UseMapReturnData => {
  const map = useMap();

  /* A project the map cannot place is dropped rather than marked at 0,0 — and a NaN half would
     otherwise reach fitBounds, which leaves the map blank rather than merely misplacing a marker. */
  const coordinatesList = useMemo(
    () =>
      coordinates.flatMap<ProjectCoordinateType>(({ name, coordinate }) => {
        const parsedCoordinate = toCoordinate(coordinate);

        return parsedCoordinate ? [{ name, ...parsedCoordinate }] : [];
      }),
    [coordinates]
  );

  const center = useMemo(() => {
    if (coordinatesList.length === 0) {
      return { lat: 0, lng: 0 };
    }
    const latValues = coordinatesList.map((coordinate) => coordinate.lat);
    const lngValues = coordinatesList.map((coordinate) => coordinate.lng);

    const minLat = Math.min(...latValues);
    const maxLat = Math.max(...latValues);
    const minLng = Math.min(...lngValues);
    const maxLng = Math.max(...lngValues);

    const centerLat = minLat + (maxLat - minLat) * 0.5;
    const centerLng = minLng + (maxLng - minLng) * 0.5;

    return { lat: centerLat, lng: centerLng };
  }, [coordinatesList]);

  useEffect(() => {
    if (map && coordinatesList.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      for (let i = 0; i < coordinatesList.length; i++) {
        bounds.extend({
          lat: coordinatesList[i].lat,
          lng: coordinatesList[i].lng,
        });
      }

      if (coordinatesList.length === 1) {
        if (fullWidth) {
          google.maps.event.addListenerOnce(map, "bounds_changed", () => {
            map.setZoom(MapConstants.DEFAULT_ZOOM_LEVEL_FULL_WIDTH);
          });
        } else {
          google.maps.event.addListenerOnce(map, "bounds_changed", () => {
            map.setZoom(MapConstants.DEFAULT_ZOOM_LEVEL);
          });
        }
      } else {
        google.maps.event.addListenerOnce(map, "bounds_changed", () => {
          map.setZoom((map.getZoom() as number) - 1);
        });
      }

      map.fitBounds(bounds);
    }
  }, [map, coordinatesList, fullWidth]);

  return {
    coordinatesList,
    center,
  };
};
